import { useMemo } from 'react';
import { gql } from '@soundxyz/gql-string';
import { RefetchOnComplete } from '@graphql/helpers/effects';
import type { FragmentType, SortOrder } from '@graphql/reactQuery';
import {
  AddReleaseToPlaylistDocument,
  getFragment,
  LikeReleaseDocument,
  PlaylistHookDocument,
  PlaylistHookInfoFragmentDoc,
  PlaylistPageNewDocument,
  RemoveReleaseFromShelfDocument,
  UnlikeReleaseDocument,
  useInfiniteQuery,
} from '@graphql/reactQuery';
import type { PlaylistRelease } from './Page/store';
import { PAGE_SIZE, setPlaylistOrderedsIds, usePlaylistState } from './Page/store';

export const INITIAL_PLAYLIST_RELEASES_COUNT = 15;

RefetchOnComplete({
  trigger: [
    AddReleaseToPlaylistDocument,
    RemoveReleaseFromShelfDocument,
    LikeReleaseDocument,
    UnlikeReleaseDocument,
  ],
  refetch: [PlaylistHookDocument, PlaylistPageNewDocument],
});

/*#__PURE__*/ gql(/* GraphQL */ `
  query PlaylistHook(
    $id: UUID!
    $pagination: ShelfOrderedReleaseCursorConnectionArgs!
    $showPreviewPlaylistRow: Boolean!
    $showPlaylistRow: Boolean!
  ) {
    shelf: playlistV2(id: $id) {
      id
      trackIds
      releaseIds
      orderedReleases(pagination: $pagination) {
        edges {
          node {
            id
            index
            addedAt
            release {
              id
            }
            # this helps us only fetch the data we will be rendering
            ...PlaylistRowReleaseInfo @include(if: $showPlaylistRow)
            ...PreviewPlaylistRow @include(if: $showPreviewPlaylistRow)
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  }

  fragment PlaylistHookInfo on Shelf {
    id
    releaseCount
  }
`);

type UsePlaylistBaseProps = {
  playlist: FragmentType<PlaylistHookInfoFragmentDoc>;
  /** How many releases to be shown */
  limit?: number;
  /** Row component you are trying to render with this hook. */
  type: 'previewPlaylistRow' | 'playlistRow';
};

const emptyList: [] = [];

export type PlaylistSortProps =
  | {
      indexSort: SortOrder;
      dateSort?: undefined;
    }
  | { dateSort: SortOrder; indexSort?: undefined };

export function usePlaylist({
  playlist: playlistFragment,
  limit,
  type,
  ...props
}: UsePlaylistBaseProps & PlaylistSortProps) {
  const { id: playlistId, releaseCount } = getFragment(
    PlaylistHookInfoFragmentDoc,
    playlistFragment,
  );

  const sortType = props.dateSort ? 'date' : 'index';
  const sortOrder = props.dateSort || props.indexSort;

  const {
    orderedList,
    isInitialLoading: isLoading,
    isFetchingNextPage,
    hasNextPage = false,
    loadMoreNextPage,
    entityStore: { nodes: playlistReleaseNodes },
    error,
  } = useInfiniteQuery(PlaylistHookDocument, {
    filterQueryKey: {
      playlistId,
      type,
      sortType,
      sortOrder,
    },
    getNextPageParam({ data: { shelf } }) {
      return (
        shelf?.orderedReleases.pageInfo.hasNextPage && {
          after: shelf.orderedReleases.pageInfo.endCursor,
        }
      );
    },
    variables({ pageParam }) {
      return {
        id: playlistId,
        showPreviewPlaylistRow: type === 'previewPlaylistRow',
        showPlaylistRow: type === 'playlistRow',
        pagination: {
          first: limit ?? PAGE_SIZE,
          after: pageParam?.after ?? null,
        },
      };
    },
    list({ shelf }) {
      return shelf?.orderedReleases.edges.map(edge => edge.node);
    },
    uniq({ release }) {
      return release.id;
    },
    onFetchCompleted({ data }) {
      if (!data.shelf) return;

      setPlaylistOrderedsIds({
        playlistId,
        orderType: sortType,
        releaseIds: data.shelf.releaseIds,
        trackIds: data.shelf.trackIds,
      });
    },

    enabled: !!releaseCount,
    // Don't fetch on window focus if we are rendering playlists in activity feed
    // This avoids eg playlists from showing empty if the playlist items are refetched
    // while the activity feed itself is not refetched
    refetchOnReconnect: type !== 'previewPlaylistRow',
    refetchOnWindowFocus: type !== 'previewPlaylistRow',
    staleTime: 0,
  });

  const { orderedTrackIds, orderedReleaseIds, activeMintableReleaseIndex } = usePlaylistState({
    playlistId,
  });

  const releases = useMemo(() => {
    const orderedReleasesSort = orderedReleaseIds[sortType];
    if (!orderedReleasesSort) return orderedList;

    // Here we construct the list of releases based on the source of truth ordered list
    return orderedReleasesSort.reduce((acc: PlaylistRelease[], releaseId) => {
      const dataReleaseId = playlistReleaseNodes[releaseId];

      if (dataReleaseId) acc.push(dataReleaseId);

      return acc;
    }, []);
  }, [orderedReleaseIds, sortType, orderedList, playlistReleaseNodes]);

  return {
    releases,
    trackIDs: orderedTrackIds[sortType] || emptyList,
    hasNextPage,
    isLoading,
    isFetchingNextPage,

    loadMore: loadMoreNextPage,
    activeMintableReleaseIndex,
    error,
  };
}
